import { useState } from "react";
import styled from "styled-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { touchSkipioApi, useSkipioApi } from "../../api";
import { useFields } from "../../hooks/use-fields";
import useExitWarning from "../../hooks/use-prevent-exit";
import { useToast } from "../../hooks/use-toast";
import { THEME_COLOR } from "../../vars";
import {
  BoxContent,
  BoxFooter,
  BoxHeader,
  BoxNavButton,
  BoxNavLeft,
  BoxNavRight,
  BoxTitle,
} from "../Box";
import Button from "../Button";
import Composer from "../Composer";
import { Close } from "../Icons";
import { SmsMessage } from "../Message";
import SidePanel, { SidePanelTrigger } from "../SidePanel";
import Stack from "../Stack";
import Title from "../Title";
import Input, { Label, HelpText } from "./../Input";
import { optOutInstructions } from "./constants";
import { useSkipioQuery } from "../../hooks/use-query";
import { fetchUser } from "../../api/users";

function KeywordDetails({
  hide,
  keywordId,
}: {
  hide: () => void;
  keywordId?: string;
}) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;
  const keywordQuery = useSkipioQuery({
    queryKey: ["keywords", keywordId],
    enabled: !!keywordId,
  });
  let keywordInfo = keywordId ? keywordQuery.data : null;
  const showToast = useToast();

  const {
    field,
    value: { messagesPerMonth, description, keyword, useCase, optInMessage },
    touched,
    reset,
    checkpoint,
  } = useFields({
    initialValue: {
      keyword: keywordInfo?.keyword || "",
      description: keywordInfo?.description || "",
      useCase: keywordInfo?.useCase || "",
      messagesPerMonth: keywordInfo?.messagesPerMonth || 0,
      optInMessage: {
        body: keywordInfo?.optInMessage || "",
      },
    },
  });
  const optOutInstMessage = optOutInstructions(
    keywordInfo?.staticBodyChunk,
    messagesPerMonth,
    keywordInfo?.slug
  );

  const hasChanged = Object.keys(touched).length > 0;
  useExitWarning(hasChanged);

  const cancel = () => {
    reset();
  };

  const save = async () => {
    if (!hasChanged) return;
    setErrors({});

    const caller = keywordId
      ? skipioApi.keywords[keywordId].put
      : skipioApi.keywords.post;

    try {
      await caller(
        {},
        {
          data: {
            keyword: {
              description,
              keyword,
              use_case: useCase,
              messages_per_month: messagesPerMonth,
              opt_in_message: optInMessage.body,
            },
          },
        }
      );

      showToast(`Keyword ${keywordId ? "Updated" : "Created"}.`, {
        success: true,
      });
      await touchSkipioApi("keywords");
      await queryClient.invalidateQueries({ queryKey: ["keywords"] });
    } catch (e: any) {
      if (e.response) {
        setErrors(e.response.data.errors);
        return;
      } else throw e;
    }

    checkpoint();
    hide();
  };

  const useCases = ["Marketing", "Something", "Something else"];
  return (
    <>
      <BoxHeader>
        <BoxTitle>{keywordInfo?.keyword || "New Keyword"}</BoxTitle>
        <BoxNavRight>
          <BoxNavButton onClick={hide}>
            <Close />
          </BoxNavButton>
        </BoxNavRight>
      </BoxHeader>
      <BoxContent>
        <Stack>
          {!keywordInfo && (
            <>
              <Title>Setup a new keyword</Title>
              <HelpText>
                Something long as why info. Something long as info. Something
                long as info. Something long as info. Something long as info.
                Something long as info. Something long as info. Something long
                as info. Something long
              </HelpText>
            </>
          )}
          <Input
            label="Keyword"
            required
            {...field("keyword")}
            sublabel="Some info o suggest what to enter. Cannot be edited once saved"
            error={errors["keyword"]}
            disabled={!!keywordInfo}
          />
          <Input
            label="Keyword Description"
            type="textarea"
            {...field("description")}
            sublabel="Some info o suggest what to enter. his could be anything"
            error={errors["description"]}
            rows={3}
          />
          <Input
            label="Intended Use"
            required
            {...field("useCase")}
            error={errors["useCase"]}
            type="select"
            sublabel="Select any one. Once selected cannot be edited"
            disabled={!!keywordInfo}
          >
            <option></option>
            {useCases.map((useCase) => (
              <option key={useCase}>{useCase}</option>
            ))}
          </Input>
          <Input
            label="Number of msgs per month"
            required
            type="number"
            {...field("messagesPerMonth")}
            sublabel="Some info o suggest what to enter. Cannot be edited once saved"
            error={errors["messagesPerMonth"]}
            disabled={!!keywordInfo}
          />
          <>
            <Label>Opt-in Auto Response</Label>
            <Composer
              {...field("optInMessage")}
              enableMessageLibrary={false}
              enableScheduling={false}
              enableRinglessVoicemail={false}
              hideVideo
              optOutInstruction={`\n\n${optOutInstMessage}`}
            />
          </>
          <>
            <Label>Opt Out Instructions</Label>
            <Composer
              value={{
                body: optOutInstMessage,
              }}
              enableMessageLibrary={false}
              enableScheduling={false}
              enableRinglessVoicemail={false}
              enableMedia={false}
              disabled
              showOnlyMessageLength
            />
          </>
          {keyword && (
            <InfoSection>
              <Top>
                By Texting <Blue>{keyword}</Blue> to{" "}
                <Blue>
                  {user.providerPhoneNumbers.length
                    ? user.providerPhoneNumbers[0].phoneNumber
                    : "your skipio phone number"}
                </Blue>
              </Top>
              your contacts will receive the following message and will be
              opted-in to your future messages.
            </InfoSection>
          )}
          <SmsMessage metadata="">
            {`${optInMessage.body}\n\n${optOutInstMessage}`}
          </SmsMessage>
        </Stack>
      </BoxContent>
      <BoxFooter>
        <Stack horizontal center>
          <BoxNavLeft>
            <Button variant="link" onClick={cancel}>
              Cancel
            </Button>
          </BoxNavLeft>
          <BoxNavRight>
            <Button disabled={!hasChanged} onClick={save}>
              Save Keyword
            </Button>
          </BoxNavRight>
        </Stack>
      </BoxFooter>
    </>
  );
}

function KeywordSidePanel({
  trigger,
  keywordId,
}: {
  trigger: SidePanelTrigger;
  keywordId?: string;
}) {
  return (
    <SidePanel
      trigger={trigger}
      content={({ hide }) => (
        <KeywordDetails hide={hide} keywordId={keywordId} />
      )}
    />
  );
}

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  line-height: 1.4em;
`;

const Top = styled.div`
  display: flex;
`;

const Blue = styled.span`
  color: ${THEME_COLOR};
  margin: 0 5px;
  font-size: 1.2em;
`;

export default KeywordSidePanel;
